import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { VENUE_EVENTS } from '@/lib/shared-types';
import { useCurrency } from '@/Hooks/useCurrency';
import { useRealtime, useRealtimeResync } from '@/Hooks/useRealtime';
import { useBranches, useBranchRooms } from '@/Pages/Operations/shared';
import { useAppSelector } from '@/Redux/store';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import PanelResult from './PanelResult';
import OrderDetailDialog from './OrderDetailDialog';
import {
  AggregatorOrderStatus,
  useAggregatorOrdersQuery,
  type AggregatorOrderLink,
} from './deliveryAggregatorsApi';
import { apiMessage, formatDateTime, toneForAggregatorStatus } from './lib';

const STATUSES = Object.values(AggregatorOrderStatus);

/** Read again every 20s. The backstop, not the primary path — see the realtime comment below for why polling alone would be too slow and a socket alone would be too little. */
const POLL_MS = 20_000;

/**
 * The gate. Every order a marketplace has sent this venue, oldest RECEIVED
 * first — the row a floor role opens the screen to answer.
 *
 * ## Realtime, and why it is subscribe *and* poll, not one or the other
 *
 * `DeliveryAggregatorOrdersService.ingestOrder` creates the Tarabeeza order
 * through `OrdersService.create`, unmodified (ADR-0028 §B1), which emits
 * `VENUE_EVENTS.ORDER_CREATED` to the order's branch room exactly like any
 * other channel's order does — read directly off `orders.service.ts:463`, not
 * assumed. `accept()` similarly calls `orders.confirm`/`orders.sendToKitchen`,
 * which emit `ORDER_CONFIRMED`/`ORDER_SENT_TO_KITCHEN`. All three are
 * subscribed below, so a fresh order and a same-tab accept both repaint within
 * a socket round trip.
 *
 * What is **not** wired to any event, read directly off the same file: this
 * module's own `reject()`/`cancel()` call `OrdersService.cancel`/`voidOrder`,
 * and `orders.service.ts` emits nothing for either
 * (`// No event: voidOrder() emits none either`) — and the aggregator link's
 * own status field (the thing this screen actually filters and sorts on) is
 * never itself a `VENUE_EVENT`; ADR-0028 §B6 sends its acknowledgements
 * through `IntegrationDeliveryService.enqueue()` directly, by design, never
 * through the venue-events bridge. A provider-initiated cancellation or
 * courier update arrives the same way, silently as far as this socket is
 * concerned. So the three subscriptions catch the time-critical case — a new
 * order must never sit unseen — and a 20s poll is the honest backstop for
 * every transition this module does not broadcast. `useRealtimeResync`
 * additionally re-reads once the socket comes back from a reconnect, so a
 * blip during which an order was rejected or accepted from another tab is not
 * still showing its pre-blip state.
 */
export default function IncomingOrdersTab() {
  const { t, i18n } = useTranslation();
  const branches = useBranches();
  const activeBranchId = useAppSelector((s) => s.ui.activeBranchId);

  const [status, setStatus] = useState<AggregatorOrderStatus | ''>(AggregatorOrderStatus.RECEIVED);
  const [branchId, setBranchId] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const effectiveBranchId = branchId || activeBranchId || undefined;

  const query = {
    limit: 100,
    status: status || undefined,
    branchId: effectiveBranchId,
  };
  const { data, isFetching, isError, error, refetch } = useAggregatorOrdersQuery(query, {
    pollingInterval: POLL_MS,
  });
  const rows = data?.items ?? [];

  const filtered = Boolean(branchId);
  const clearFilters = () => setBranchId('');

  const branchRooms = useBranchRooms(effectiveBranchId, rows);
  const onOrderEvent = useCallback(() => refetch(), [refetch]);
  useRealtime(VENUE_EVENTS.ORDER_CREATED, onOrderEvent, branchRooms);
  useRealtime(VENUE_EVENTS.ORDER_CONFIRMED, onOrderEvent, branchRooms);
  useRealtime(VENUE_EVENTS.ORDER_SENT_TO_KITCHEN, onOrderEvent, branchRooms);
  useRealtimeResync(onOrderEvent);

  const receivedCount = rows.filter((r) => r.status === AggregatorOrderStatus.RECEIVED).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('deliveryAggregators.incoming.filterStatus')} controlId="agg-status">
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as AggregatorOrderStatus | '')}
              className="w-52"
            >
              <option value="">{t('deliveryAggregators.incoming.filterAllStatuses')}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`deliveryAggregators.status.${s}`)}
                </option>
              ))}
            </Select>
          </Field>
          {branches.length > 0 && (
            <Field label={t('deliveryAggregators.incoming.filterBranch')} controlId="agg-branch">
              <Select value={branchId} onChange={(e) => setBranchId(e.target.value)} className="w-52">
                <option value="">{t('deliveryAggregators.incoming.filterAllBranches')}</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {filtered && (
            <Button variant="outline" onClick={clearFilters}>
              {t('deliveryAggregators.states.clearFilters')}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {status === AggregatorOrderStatus.RECEIVED && receivedCount > 0 && (
            <Badge tone="warning">
              {t('deliveryAggregators.incoming.waitingCount', { count: receivedCount })}
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={refetch} disabled={isFetching}>
            <Icon as={RefreshCw} />
            {t('deliveryAggregators.incoming.refresh')}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <PanelResult
            loading={isFetching && rows.length === 0}
            error={isError ? apiMessage(error, t('deliveryAggregators.states.loadFailed')) : null}
            count={rows.length}
            onRetry={refetch}
            filtered={filtered}
            onClearFilters={clearFilters}
            emptyTitle={t(
              status === AggregatorOrderStatus.RECEIVED
                ? 'deliveryAggregators.incoming.emptyReceived'
                : 'deliveryAggregators.incoming.empty',
            )}
            emptyHint={t('deliveryAggregators.incoming.emptyHint')}
          >
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>{t('deliveryAggregators.incoming.order')}</TH>
                    <TH>{t('deliveryAggregators.incoming.provider')}</TH>
                    <TH>{t('deliveryAggregators.incoming.status')}</TH>
                    <TH>{t('deliveryAggregators.incoming.gross')}</TH>
                    <TH>{t('deliveryAggregators.incoming.received')}</TH>
                  </TR>
                </THead>
                <tbody>
                  {rows.map((row) => (
                    <OrderRow key={row.id} row={row} locale={i18n.language} onOpen={() => setOpenId(row.id)} />
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </PanelResult>
        </CardContent>
      </Card>

      {openId && <OrderDetailDialog id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function OrderRow({
  row,
  locale,
  onOpen,
}: {
  row: AggregatorOrderLink;
  locale: string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const { format } = useCurrency(row.branchId);
  return (
    <TR className="cursor-pointer hover:bg-hover" onClick={onOpen}>
      <TD className="font-medium">
        {row.order?.number ?? row.externalOrderId}
        <p className="text-xs font-normal text-ink-muted">{row.externalOrderId}</p>
      </TD>
      <TD className="text-sm text-ink-muted">{row.providerKey}</TD>
      <TD>
        <Badge tone={toneForAggregatorStatus(row.status)}>{t(`deliveryAggregators.status.${row.status}`)}</Badge>
      </TD>
      <TD className="tabular-nums">{format(row.grossAmount)}</TD>
      <TD className="text-ink-muted">{formatDateTime(row.createdAt, locale)}</TD>
    </TR>
  );
}
